import { TabType } from './tabRoutes';

export interface BillingPlanContext {
  plan_id?: string | null;
  trial_eligible?: boolean;
}

export type PlanId = 'free' | 'starter' | 'pro' | 'enterprise';
export type RequiredPlanId = 'starter';

export const STARTER_PLAN_PATH = '/billing?section=plans&plan=starter';
export const getUpgradeLabel = (account: BillingPlanContext | null | undefined): string =>
  account?.trial_eligible === false ? 'Subscribe to Starter' : 'Start 7-day trial';

export type FeatureAccessKey =
  | 'workspace_mutation'
  | 'agentic_search'
  | 'prompt_run_execution'
  | 'import_lifecycle'
  | 'export_lifecycle'
  | 'connector_integrations'
  | 'webhook_integrations'
  | 'api_key_management'
  | 'mcp_integrations'
  | 'automation_integrations';

export interface LockedFeatureInfo {
  featureKey: FeatureAccessKey;
  featureName: string;
  requiredPlan: RequiredPlanId;
  reason: string;
  freePlanMessage: string;
  upgradePath: string;
}

export type LockedFeatureMap = Partial<Record<TabType, LockedFeatureInfo>>;

interface FeatureGateInfo {
  featureName: string;
  requiredPlan: RequiredPlanId;
  reason: string;
  freePlanMessage: string;
  upgradePath: string;
}

const PLAN_ORDER: Record<PlanId, number> = {
  free: 0,
  starter: 1,
  pro: 2,
  enterprise: 3,
};

const featureNames: Record<FeatureAccessKey, string> = {
  workspace_mutation: 'Workspace features',
  prompt_run_execution: 'Extraction runs',
  agentic_search: 'Agentic Search',
  import_lifecycle: 'Cloud import',
  export_lifecycle: 'Downloads and exports',
  connector_integrations: 'Cloud connectors',
  webhook_integrations: 'Webhooks',
  api_key_management: 'API keys',
  mcp_integrations: 'MCP integration',
  automation_integrations: 'Automations',
};

const FEATURE_GATES = Object.fromEntries(
  Object.entries(featureNames).map(([key, featureName]) => [key, {
    featureName,
    requiredPlan: 'starter',
    reason: 'Start a trial to unlock all Starter features.',
    freePlanMessage: 'Free includes Playground upload and processing, plus demo index browsing and search.',
    upgradePath: STARTER_PLAN_PATH,
  }]),
) as Record<FeatureAccessKey, FeatureGateInfo>;

export const TAB_FEATURES: Partial<Record<TabType, FeatureAccessKey>> = {
  workflows: 'workspace_mutation',
  realtime: 'workspace_mutation',
  marked: 'workspace_mutation',
  jobs: 'workspace_mutation',
  usage: 'workspace_mutation',
  automations: 'automation_integrations',
  connectors: 'connector_integrations',
  apikeys: 'api_key_management',
  mcp: 'mcp_integrations',
  webhooks: 'webhook_integrations',
};

const normalizePlanId = (planId: string | null | undefined): string => (
  planId || ''
).trim().toLowerCase();

const isKnownPlanId = (planId: string): planId is PlanId => (
  Object.prototype.hasOwnProperty.call(PLAN_ORDER, planId)
);

export const getFeatureGate = (featureKey: FeatureAccessKey): LockedFeatureInfo => {
  const gate = FEATURE_GATES[featureKey];
  return {
    featureKey,
    featureName: gate.featureName,
    requiredPlan: gate.requiredPlan,
    reason: gate.reason,
    freePlanMessage: gate.freePlanMessage,
    upgradePath: gate.upgradePath,
  };
};

export const hasPlanAtLeast = (
  account: BillingPlanContext | null | undefined,
  requiredPlan: RequiredPlanId,
): boolean => {
  const planId = normalizePlanId(account?.plan_id);
  if (!planId) return false;
  if (!isKnownPlanId(planId)) return false;
  const currentOrder = PLAN_ORDER[planId];
  return currentOrder >= PLAN_ORDER[requiredPlan];
};

export const hasFeatureAccess = (
  featureKey: FeatureAccessKey,
  account: BillingPlanContext | null | undefined,
): boolean => {
  const gate = FEATURE_GATES[featureKey];
  return hasPlanAtLeast(account, gate.requiredPlan);
};

export const getLockedFeature = (
  featureKey: FeatureAccessKey,
  account: BillingPlanContext | null | undefined,
): LockedFeatureInfo | null => {
  if (hasFeatureAccess(featureKey, account)) return null;
  const gate = getFeatureGate(featureKey);
  return account?.trial_eligible === false
    ? { ...gate, reason: 'Subscribe to Starter to unlock all features.' }
    : gate;
};

export const getLockedFeatureForTab = (
  tab: TabType,
  account: BillingPlanContext | null | undefined,
): LockedFeatureInfo | null => {
  const featureKey = TAB_FEATURES[tab];
  return featureKey ? getLockedFeature(featureKey, account) : null;
};

export const getLockedTabs = (
  account: BillingPlanContext | null | undefined,
): LockedFeatureMap => {
  return (Object.keys(TAB_FEATURES) as TabType[]).reduce<LockedFeatureMap>((locked, tab) => {
    const feature = getLockedFeatureForTab(tab, account);
    if (feature) locked[tab] = feature;
    return locked;
  }, {});
};
